import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import {from, Observable, of, throwError} from 'rxjs';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {RedirectHandlerService} from '../../services/test/redirect-handler.service';
import {Router} from '@angular/router';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {AuthenticationResult, InteractionStatus} from '@azure/msal-browser';
import {TenantContextService} from '../../../services/tenant-context.service';
import {custodisFallbackTenantId} from '../../../config/custodis-tenant';
import {apimKeyFor} from '../../../core/apim/apim-subscription';

// Records that we already sent the user through an interactive sign-in because THIS API origin
// returned 401. Keyed per origin and persisted, because the page load that loginRedirect() causes
// wipes any in-memory flag — see reauthenticateOnce().
const REAUTH_ATTEMPTED_KEY = 'bc_reauth_attempted';

/**
 * Which API a request URL belongs to, for the once-per-API re-auth guard: its origin, or '' for
 * relative URLs (same-origin, never a cross-API 401).
 *
 * Behind APIM every module shares one host, so the origin alone would let a healthy API clear the
 * guard for a permanently-401ing one and loop the whole app through sign-in. There the first path
 * segment (the APIM API suffix — /corporate, /accounting, ...) is part of the key.
 */
function originOf(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    if (!apimKeyFor(u.href)) return u.origin;
    return `${u.origin}/${u.pathname.split('/')[1] ?? ''}`;
  } catch {
    return '';
  }
}

/**
 * A 401 written by the APIM gateway itself (missing/invalid subscription key, API not in the key's
 * product) rather than by the API behind it. Signing in again cannot fix those.
 */
function isApimGateway401(err: HttpErrorResponse): boolean {
  const message = err.error?.message;
  return typeof message === 'string' && message.startsWith('Access denied due to');
}

@Injectable()
export class MsalCustomerInterceptor implements HttpInterceptor {
  private customerToken?: AuthenticationResult;
  private employeeTokens = new Map<string, AuthenticationResult>();
  private redirecting = false;
  private interactionStatus: InteractionStatus = InteractionStatus.Startup;

  constructor(
    private readonly employeeMsalService: MsalService,
    private readonly customerMsalService: RedirectHandlerService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly router: Router,
    private readonly tenantContext: TenantContextService
  ) {
    this.msalBroadcastService.inProgress$.subscribe(status => {
      this.interactionStatus = status;
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.startsWith('https://graph.microsoft.com')) {
      return next.handle(request);
    }
    let token$: Observable<AuthenticationResult>;
    const url = this.router.url;
  
    if (url.includes('/auth/form/') && this.customerMsalService.activeAccount) {
      token$ = this.getValidCustomerToken(() =>
        this.customerMsalService.acquireTokenSilent()
      );
    } else {
      const accounts = this.employeeMsalService.instance.getAllAccounts();
      if (accounts.length === 0) {
        // Only kick off a redirect if MSAL is not already mid-interaction.
        // Firing loginRedirect() while another PCA (e.g. the M365 Graph instance)
        // is settling its own redirect throws BrowserAuthError: interaction_in_progress.
        if (!this.redirecting && this.interactionStatus === InteractionStatus.None) {
          this.redirecting = true;
          console.warn('No active employee account - redirecting to login.');
          this.employeeMsalService.loginRedirect();
        }
        return throwError(() => new Error('No active account - redirecting to login'));
      }
      const account = accounts[0];
      const scopes =
        request.method === 'GET'
          ? environment.apiConfig.IdentityAPI.scopes.read
          : environment.apiConfig.IdentityAPI.scopes.write;
  
      token$ = this.getValidEmployeeToken(scopes, () =>
        this.employeeMsalService.instance.acquireTokenSilent({
          account,
          scopes: [...scopes],
        })
      );
    }
  
    return token$.pipe(
      switchMap((result: AuthenticationResult) => {
        const headers: Record<string, string> = {
          Authorization: `Bearer ${result.accessToken}`
        };
        // Tell the backend module which tenant database to use. Only our module APIs need it.
        if (this.isModuleApi(request.url)) {
          // Custodis alone cannot run un-tenanted: it 401s a request with no tenant instead of
          // falling back to a control database like the other modules, so on the general system
          // (no Billing tenant) it gets a company-derived id. See custodisFallbackTenantId — that
          // fallback must never reach another module, which is why it is gated on the URL.
          // `||` not `??`: a bootstrap that answered with an empty tenantId is as un-tenanted as
          // a missing one, and `??` would pass '' straight through and skip the fallback.
          const tenantId = this.tenantContext.tenantId
            || (this.isCustodisApi(request.url) ? custodisFallbackTenantId() : null);
          if (tenantId) headers['X-BC-Tenant'] = tenantId;
        }
        const cloned = request.clone({ setHeaders: headers });
        return next.handle(cloned).pipe(
          // A success from this origin proves signing in fixed it, so a future 401 from the same
          // origin is worth another interactive attempt. Scoped per origin on purpose: clearing
          // globally would let a healthy API (Identity, Corporate) reset the guard on every page
          // load and hand a permanently-401ing API an unlimited supply of redirects.
          tap(event => {
            if (event instanceof HttpResponse) this.clearReauthAttempt(originOf(request.url));
          }),
        );
      }),
      catchError(err => {
        // This catch sits BELOW next.handle(), so it sees the response as well as the token
        // acquisition. Labelling everything "Token acquisition failed" sent people hunting for an
        // auth bug when the real cause was the request never leaving the browser: a `status: 0`
        // HttpErrorResponse is a DNS/CORS/offline failure (an inventory_api host that no longer
        // resolved produced exactly that), not a token problem.
        if (err instanceof HttpErrorResponse) {
          const reason = err.status === 0
            ? 'request never reached the server (DNS, CORS or offline)'
            : `server responded ${err.status}`;
          console.error(`API call failed — ${reason}:`, err.url, err);
        } else {
          console.error('Token acquisition failed:', err);
        }
        if (err.status === 401 && !(err instanceof HttpErrorResponse && isApimGateway401(err))) {
          this.reauthenticateOnce(originOf(request.url));
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Send the user through an interactive sign-in for a 401 — but only once per API origin, until
   * that origin succeeds again.
   *
   * `redirecting` alone is not enough: loginRedirect() navigates the whole browser away and back,
   * which rebuilds this interceptor and resets the flag. So a 401 that signing in cannot fix — an
   * API deployed with the wrong token audience, say — becomes an endless redirect loop that takes
   * the whole app down, not just the module owning the failing endpoint. After one attempt per
   * origin the error is allowed to surface instead.
   */
  private reauthenticateOnce(origin: string): void {
    if (this.redirecting || this.interactionStatus !== InteractionStatus.None) return;
    if (this.attemptedOrigins().includes(origin)) return;

    this.writeAttemptedOrigins([...this.attemptedOrigins(), origin]);
    this.redirecting = true;
    this.employeeMsalService.loginRedirect();
  }

  private clearReauthAttempt(origin: string): void {
    const remaining = this.attemptedOrigins().filter(o => o !== origin);
    if (remaining.length !== this.attemptedOrigins().length) this.writeAttemptedOrigins(remaining);
  }

  private attemptedOrigins(): string[] {
    try {
      const raw = sessionStorage.getItem(REAUTH_ATTEMPTED_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writeAttemptedOrigins(origins: string[]): void {
    try {
      if (origins.length) sessionStorage.setItem(REAUTH_ATTEMPTED_KEY, JSON.stringify(origins));
      else sessionStorage.removeItem(REAUTH_ATTEMPTED_KEY);
    } catch {
      // sessionStorage unavailable (private mode / blocked). The in-memory `redirecting` flag
      // still prevents a burst within one page load; we just cannot survive the reload.
    }
  }

  // --- CUSTOMER (single token type) ---
  private getValidCustomerToken(
    acquire: () => Promise<AuthenticationResult>
  ): Observable<AuthenticationResult> {
    if (this.customerToken && !this.isExpired(this.customerToken.expiresOn)) {
      return of(this.customerToken);
    }
    return from(acquire()).pipe(
      tap(result => (this.customerToken = result))
    );
  }

  // --- EMPLOYEE (different scopes) ---
  private getValidEmployeeToken(
    scopes: string[],
    acquire: () => Promise<AuthenticationResult>
  ): Observable<AuthenticationResult> {
    const scopeKey = scopes.sort().join(' '); // normalize scope key
    const cached = this.employeeTokens.get(scopeKey);

    if (cached && !this.isExpired(cached.expiresOn)) {
      return of(cached);
    }

    return from(acquire()).pipe(
      tap(result => this.employeeTokens.set(scopeKey, result))
    );
  }

  private isExpired(expiresOn?: Date | null): boolean {
    if (!expiresOn) return true;
    return expiresOn.getTime() - Date.now() < 2 * 60 * 1000;
  }

  // Attach the tenant header to EVERY call to one of our backend module APIs so they all read the
  // tenant's databases. Never to Graph, the Billing/licensing API, or third parties (a custom
  // header would also trigger a CORS preflight those hosts may reject).
  private isModuleApi(url: string): boolean {
    const cfg = environment.apiConfig as any;
    const billing = cfg.billingAPI?.endpoint as string | undefined;
    if (url.startsWith('https://graph.microsoft.com')) return false;
    if (billing && url.startsWith(billing)) return false;

    // 1) Exact configured endpoints (covers localhost and any env).
    const endpoints = [
      cfg.IdentityAPI?.endpoint,
      cfg.corporateAPI?.endpoint,
      cfg.legacyInvoicingApi?.endpoint,
      // Custodis needs X-BC-Tenant on every call: its TenantContextMiddleware 401s an
      // authenticated request that carries neither the header nor a tid claim, and B2C
      // tokens have no tid. Keep in step with the custodis route in app-routing.module.ts.
      // On the general system there is no Billing tenant, so isCustodisApi() supplies a
      // company-derived one instead — see custodisFallbackTenantId.
      cfg.custodisAPI?.endpoint,
      // The accounting API resolves its own tenant database from this header (and forwards it on
      // to Corporate/Identity when it reads the chart of accounts or a company). Without it the
      // API falls back to its control database, so one tenant reads another's ledger.
      cfg.accountingAPI?.endpoint,
      // The migration API forwards this header on to BC_Identity when it resolves the current
      // company. Without it Identity falls back to its control database and answers for the
      // wrong tenant, which surfaces as "could not load company N".
      (environment as any).migration_api,
      (environment as any).invoicing_api,
      // Ticketing records the tenant on the ticket rather than using it to pick a database — it keeps
      // one database for the whole platform, because the developers' board reads across tenants. The
      // header is still required: without it a customer's ticket is filed as untenanted and lands in
      // the wrong scope. See docs/ticketing/CODEBASE-RESOLUTIONS.md §2.
      cfg.ticketingAPI?.endpoint,
    ].filter(Boolean) as string[];
    if (endpoints.some(e => url.startsWith(e))) return true;

    // 2) Robust fallback: any of our module API hosts (dev or prod suffixes), so no call is missed
    //    if it uses a slightly different/hardcoded base than the configured endpoint.
    return /:\/\/(bc-id-api|bc-corp-api|bc-inv-api|bc-inv-legacy-api|bc-migration-api|bc-accounting-api|bc-custodis-api|bc-ticketing-api)[a-z0-9.-]*\.azurewebsites\.net/i.test(url);
  }

  // Custodis specifically — the only module that gets the company-derived tenant fallback.
  // Matched the same two ways as isModuleApi (configured endpoint, then host pattern) so a URL can
  // never be a module API without also being recognised here and silently lose the fallback.
  private isCustodisApi(url: string): boolean {
    const endpoint = (environment.apiConfig as any).custodisAPI?.endpoint as string | undefined;
    if (endpoint && url.startsWith(endpoint)) return true;
    return /:\/\/bc-custodis-api[a-z0-9.-]*\.azurewebsites\.net/i.test(url);
  }
}
